"use client";

import { RiLockLine, RiUserLine } from "@remixicon/react";
import type { UserSearchResult } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import Image from "next/image";
import { getDisplayAvatarUrl } from "@workspace/shared-utils";

export interface UserResultProps {
	user: UserSearchResult;
	isSelected: boolean;
	onSelect: () => void;
}

export function UserResult({ user, isSelected, onSelect }: UserResultProps) {
	const avatarUrl = getDisplayAvatarUrl({
		manualAvatarUrl: user.manual_avatar_url,
		googleAvatarUrl: user.google_avatar_url,
		avatarSource: user.avatar_source as "google" | "manual" | "none" | null | undefined,
	});

	return (
		<button
			type="button"
			disabled={!user.username}
			className={cn(
				"flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors",
				user.username
					? isSelected
						? "bg-muted"
						: "hover:bg-muted"
					: "cursor-not-allowed opacity-50",
			)}
			onClick={onSelect}
		>
			{avatarUrl ? (
				<Image
					src={avatarUrl}
					alt={`@${user.username}`}
					width={32}
					height={32}
					className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
				/>
			) : (
				<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted">
					<RiUserLine className="h-4 w-4 text-muted-foreground" />
				</div>
			)}
			<div className="flex flex-1 flex-col">
				<span className="font-medium text-sm">
					@{user.username || "anonymous"}
				</span>
			</div>

			{/* Show lock icon for private accounts */}
			{user.is_private && (
				<div
					className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
					title="Private account"
					aria-label="Private account"
				>
					<RiLockLine className="h-3 w-3" />
				</div>
			)}
		</button>
	);
}
